import { useMemo, useState, FormEvent } from 'react';
import type { FormConfig, SubmissionStatus } from '../types/forms';
import { validateField } from '../utils/formValidation';
import { submitForm } from '../utils/formSubmission';

type Values = Record<string, string>;
type Errors = Record<string, string | undefined>;

export function useFormSubmission(config: FormConfig) {
  const initialValues = useMemo<Values>(
    () => Object.fromEntries(config.fields.map((f) => [f.name, ''])),
    [config]
  );

  const [values, setValues] = useState<Values>(initialValues);
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [serverMessage, setServerMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const fieldByName = (name: string) => config.fields.find((f) => f.name === name);

  const setField = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    const field = fieldByName(name);
    if (field && touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(field, value) }));
    }
  };

  const blurField = (name: string) => {
    const field = fieldByName(name);
    if (!field) return;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(field, values[name]) }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const nextErrors: Errors = {};
    config.fields.forEach((f) => {
      const msg = validateField(f, values[f.name]);
      if (msg) nextErrors[f.name] = msg;
    });
    setErrors(nextErrors);
    setTouched(Object.fromEntries(config.fields.map((f) => [f.name, true])));

    const firstInvalid = config.fields.find((f) => nextErrors[f.name]);
    if (firstInvalid) {
      document.getElementById(`${config.id}-${firstInvalid.name}`)?.focus();
      return;
    }

    // Spam bots fill hidden fields; silently ignore those submissions.
    if (honeypot) return;

    setStatus('submitting');
    setServerMessage('');

    try {
      const payload: Record<string, string> = {};
      config.fields.forEach((f) => {
        const v = values[f.name].trim();
        payload[f.label] = f.type === 'checkbox' ? (v ? 'Yes' : 'No') : v || '—';
      });
      payload['Form'] = config.formName;
      payload['Submitted from'] = window.location.href;

      const emailField = config.fields.find((f) => f.type === 'email');
      await submitForm(payload, {
        subject: config.subject,
        replyTo: emailField ? values[emailField.name].trim() : undefined,
      });
      setStatus('success');
    } catch (err) {
      setServerMessage(err instanceof Error ? err.message : '');
      setStatus('error');
    }
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setStatus('idle');
    setServerMessage('');
  };

  return {
    values,
    errors,
    status,
    serverMessage,
    honeypot,
    setHoneypot,
    setField,
    blurField,
    handleSubmit,
    reset,
  };
}
